import { forEach } from "lodash-es";

import { CkeditorConstants } from "./ckeditorconstants";


const {
  helperAttributes: { PARENT_CITATION, CITATION_COUNT, RESOURCE },
  htmlAttributes: { ID },
  operation: { UNDO },
  commentAttributes: { ATTRIBUTES },
  htmlElement: { TABLE },
} = CkeditorConstants;

/**
 * Used for building the citation reference array
 * @param {Array} citationRefs
 * @param {Map<string, HTMLElement>} domRoots
 * @returns citationRef array
 */
export const buildCitationRefs = (citationRefs = [], editor, operation) => {


  const editingRootDomElement = editor.editing.view.domRoots.get("main");
  const citations = [];
  for (const eachRef of citationRefs) {
    const attributes = eachRef.hasOwnProperty(ATTRIBUTES)
      ? eachRef.attributes
      : null;

    let parentCitation, citationCount, id;

    const currentCitedRefsInDoc = [];

    if (operation) {
      const range = editor.model.createRangeIn(editor.model.document.getRoot());
      for (const item of range.getItems({ ignoreElementEnd: true })) {
        if (
          item.hasAttribute(RESOURCE) &&
          item.getAttribute(RESOURCE) === attributes.get(RESOURCE)
        ) {
          currentCitedRefsInDoc.push(item);
        }
      }
    }

    if (attributes) {
      if (operation === UNDO) {
        parentCitation = currentCitedRefsInDoc.length
          ? currentCitedRefsInDoc[0].getAttribute(ID)
          : attributes.get(ID);

        citationCount = currentCitedRefsInDoc.length
          ? currentCitedRefsInDoc[0].getAttribute(CITATION_COUNT)
          : null;
        id = currentCitedRefsInDoc.length
          ? currentCitedRefsInDoc[0].getAttribute(ID)
          : attributes.get(ID);
      } else {
        parentCitation = attributes.get(PARENT_CITATION);
        citationCount = attributes.get(CITATION_COUNT);
        id = attributes.get(ID);
      }
    } else {
      parentCitation = eachRef.getAttribute(PARENT_CITATION);
      citationCount = eachRef.getAttribute(CITATION_COUNT);
      id = eachRef.getAttribute(ID);
    }

    if (Boolean(parentCitation)) {
      const isFirstCitedAlreadyAvailableIndex = citations.findIndex(
        (item) => item.currentDeletedId === parentCitation
      );
      if (isFirstCitedAlreadyAvailableIndex > -1) {
        citations[isFirstCitedAlreadyAvailableIndex].subsequentRefs.push(id);
      } else {
        if (operation === UNDO) {
          citations.push({
            isUndoRedo: true,
            resource: currentCitedRefsInDoc.length
              ? currentCitedRefsInDoc[0].getAttribute(RESOURCE)
              : attributes.get(RESOURCE),
            parentCitationId: parentCitation,
            isSubsequentCitationRef: true,
            isDeleteDialogRequired: false,
            subsequentRefs: currentCitedRefsInDoc.length
              ? currentCitedRefsInDoc.map((ref) => ref.getAttribute(ID))
              : [],
            newSubsequentCitedRefs: currentCitedRefsInDoc.length
              ? currentCitedRefsInDoc.map((ref) => ref.getAttribute(ID))
              : [],
            currentDeletedId: id,
            citationCount: `[${citationCount}]`,
            citationsTotalCount: Array.from(
              editingRootDomElement.querySelectorAll(
                `[${CITATION_COUNT}='${citationCount}']`
              )
            ).length,
          });
        } else {
          citations.push({
            parentCitationId: parentCitation,
            isSubsequentCitationRef: true,
            isDeleteDialogRequired: false,
            subsequentRefs: [],
            currentDeletedId: id,
            citationCount: `[${citationCount}]`,
            citationsTotalCount: Array.from(
              editingRootDomElement.querySelectorAll(
                `[${CITATION_COUNT}='${citationCount}']`
              )
            ).length,
          });
        }
      }
    } else {

      // When user 'Discard Suggestion' of first cited citation then it's immediate next subsequent cited citation will be first cited citation
      // data-parentcitation attribute in next subsequent cited citation will be removed and will become first cited citation
      // id of new first cited citation ( immediate subsequent cited citation ) will be replaced with id of discarded first cited citation

      let additionCitationAttr = {
        newSubsequentCitedRefs: [],
        newFirstCitedId: "",
      };
      const totalCount = Array.from(
        editingRootDomElement.querySelectorAll(
          `[${CITATION_COUNT}='${citationCount}']`
        )
      ).length;

      const currentSubsequentCitedRefs = [];

      if (!parentCitation && totalCount > 1) {
        const newSubsequentCitedRefs = [...editor.editing.view.domRoots.get('main').querySelectorAll((`[${PARENT_CITATION}='${id}']`))].map(item => item.getAttribute('id'));

        const range = editor.model.createRangeIn(editor.model.document.getRoot());
        for (const item of range.getItems({ ignoreElementEnd: true })) {
          item.getAttribute(PARENT_CITATION) === id && currentSubsequentCitedRefs.push(item);
        }

        editor.model.change(writer => {
          for (const [index, entries] of currentSubsequentCitedRefs.entries()) {
            if (index === 0) {
              writer.removeAttribute(PARENT_CITATION, entries);
              writer.setAttribute(ID, id, entries);
            } else {
              writer.setAttribute(PARENT_CITATION, id, entries)
            }
          }
        });

        additionCitationAttr = {
          newSubsequentCitedRefs,
          newFirstCitedId: id
        }
      }

      citations.push({
        ...additionCitationAttr,
        currentDeletedId: id,
        parentCitationId: !parentCitation && id || parentCitation,
        subsequentRefs: [],
        isSubsequentCitationRef: Boolean(parentCitation),
        isDeleteDialogRequired: false,
        citationCount: `[${citationCount}]`,
        citationsTotalCount: Array.from(
          editingRootDomElement.querySelectorAll(
            `[${CITATION_COUNT}='${citationCount}']`
          )
        ).length,
      });
    }
  }
  return citations;
};

/**
 * Removes a non numbered elements/textproxy from document based on trackchanges.
 * @param { CKEditor5 } editor
 */
export const removeNonNumberedCitationItems = (editor) => {
  const selection = editor.model.document.selection;
  const isNotTable = selection?.getSelectedElement()?.name === TABLE
  const ranges = selection.getRanges();

  if (isNotTable) return;

  for (const range of ranges) {
    forEach([...range.getItems()], item => {
      const isRemovableItem = item.is('$text') || item.is('$textProxy') || (item.is('element') && ![...item.getChildren()].length);

      if (isRemovableItem) {
        removeNonNumberedCitationItemsFromDocument(editor, item);
        return;
      }

      const children = [...item.getChildren()];
      if (children.length) {
        forEach(children, child => {
          removeNonNumberedCitationItemsFromDocument(editor, child);
        });
      }
    });
  }
};

const removeNonNumberedCitationItemsFromDocument = (editor, item) => {
  editor.model.change((writer) => {
    const isTrackingChanges = editor.commands.get('trackChanges').value;
    const isTrackChangesEnabled = editor.plugins.get('TrackChanges').isEnabled;
    if (item.name !== "numberedcitation") {
      const itemRange = editor.model.createRangeOn(item);

      if (isTrackingChanges && isTrackChangesEnabled) {
        editor.plugins.get('TrackChangesEditing').markDeletion(itemRange);
      } else {
        writer.remove(item);
      }
    }
  });
};